import React, { useState, useEffect } from 'react';
import { DentalSeverityBadge } from '../DentalSeverityBadge';
import {
  RCA_FISHBONE_CATEGORIES
} from '../../lib/riskConstants';

/**
 * Modern Case Detail Modal with Tabbed Navigation (Overview & Photos, RCA & CAPA, CSSD Traceability)
 */
export default function RiskCaseDetailModal({
  isOpen = false,
  caseItem = null,
  onClose,
  onUpdateInvestigation,
  onTriggerRecall,
  onOpenLightbox,
  isSavingInvestigation = false,
  currentUser = null,
  userRole = '',
  isMobile = false
}) {
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'rca' | 'cssd'
  const [investigationData, setInvestigationData] = useState({
    status: 'pending',
    investigator_name: '',
    rca_fishbone_category: '',
    root_cause_analysis: '',
    corrective_action: ''
  });

  useEffect(() => {
    if (caseItem) {
      setInvestigationData({
        status: caseItem.status || 'pending',
        investigator_name:
          caseItem.investigator_name ||
          (currentUser?.name || currentUser?.email?.split('@')[0] || ''),
        rca_fishbone_category: caseItem.rca_fishbone_category || '',
        root_cause_analysis: caseItem.root_cause_analysis || '',
        corrective_action: caseItem.corrective_action || ''
      });
      setActiveTab('overview');
    }
  }, [caseItem, currentUser]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !caseItem) return null;

  const isCriticalOrSterile =
    caseItem.risk_domain === 'cssd' ||
    caseItem.category?.includes('Sterilization') ||
    caseItem.category?.includes('ฆ่าเชื้อ') ||
    caseItem.category?.includes('ล้มเหลว') ||
    caseItem.severity_score >= 5 ||
    ['E', 'F', 'G', 'H', 'I', '4', '5'].includes(String(caseItem.severity_level || '').toUpperCase());

  const handleSaveRca = (e) => {
    e?.preventDefault();
    onUpdateInvestigation?.(caseItem, investigationData);
  };

  return (
    <div
      className="risk-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="risk-case-modal-title"
    >
      <div className="risk-modal-box" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div
          style={{
            padding: '16px 24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#f8fafc',
            gap: '12px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
            <span style={{ fontSize: '26px', flexShrink: 0 }}>🛡️</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: '800', color: '#0284c7', fontSize: '1.05em' }}>
                  {caseItem.incident_number}
                </span>
                <DentalSeverityBadge level={caseItem.severity_level} domain={caseItem.risk_domain} size="sm" />
                {caseItem.recall_batch_triggered && (
                  <span
                    style={{
                      fontSize: '0.68em',
                      fontWeight: '800',
                      backgroundColor: '#fee2e2',
                      color: '#dc2626',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}
                  >
                    🚨 RECALL TRIGGERED
                  </span>
                )}
              </div>
              <h3
                id="risk-case-modal-title"
                style={{
                  margin: '3px 0 0 0',
                  fontSize: '1.15em',
                  color: '#0f172a',
                  fontWeight: '800',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {caseItem.title}
              </h3>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '22px',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: '4px 8px',
              flexShrink: 0
            }}
            title="ปิดหน้าต่าง (Esc)"
          >
            ✕
          </button>
        </div>

        {/* Modal Tabs Navigation */}
        <div className="risk-modal-tabs">
          <button
            type="button"
            className={`risk-modal-tab ${activeTab === 'overview' ? 'risk-modal-tab--active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <span>📝</span>
            <span>ข้อมูลเคส & ภาพหลักฐาน</span>
          </button>
          <button
            type="button"
            className={`risk-modal-tab ${activeTab === 'rca' ? 'risk-modal-tab--active' : ''}`}
            onClick={() => setActiveTab('rca')}
          >
            <span>🔬</span>
            <span>RCA & CAPA (การสอบสวน)</span>
          </button>
          <button
            type="button"
            className={`risk-modal-tab ${activeTab === 'cssd' ? 'risk-modal-tab--active' : ''}`}
            onClick={() => setActiveTab('cssd')}
          >
            <span>🧼</span>
            <span>การติดตาม CSSD & Recall</span>
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div
          className="risk-scrollbar"
          style={{
            padding: '20px 24px',
            overflowY: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '18px'
          }}
        >
          {/* ================= TAB 1: OVERVIEW & EVIDENCE ================= */}
          {activeTab === 'overview' && (
            <>
              {/* Grid Information */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                  gap: '12px',
                  backgroundColor: '#f8fafc',
                  padding: '16px',
                  borderRadius: '14px',
                  border: '1px solid #e2e8f0',
                  fontSize: '0.88em'
                }}
              >
                <div>
                  <span style={{ color: '#64748b' }}>วัน-เวลาเกิดเหตุ:</span>
                  <br />
                  <strong>
                    {caseItem.incident_date
                      ? new Date(caseItem.incident_date).toLocaleString('th-TH')
                      : '-'}
                  </strong>
                </div>
                <div>
                  <span style={{ color: '#64748b' }}>สถานที่เกิดเหตุ:</span>
                  <br />
                  <strong>📍 {caseItem.location}</strong>
                </div>
                <div>
                  <span style={{ color: '#64748b' }}>ประเภทความเสี่ยง:</span>
                  <br />
                  <strong>
                    {caseItem.risk_domain === 'clinical'
                      ? '🩺 ทางคลินิก (Clinical)'
                      : caseItem.risk_domain === 'cssd'
                      ? '🧼 งานจ่ายกลางและฆ่าเชื้อ (CSSD)'
                      : '🏢 ทั่วไป/สิ่งแวดล้อม (Non-Clinical)'}
                  </strong>
                </div>
                <div>
                  <span style={{ color: '#64748b' }}>หมวดหมู่:</span>
                  <br />
                  <strong>{caseItem.category}</strong>
                </div>
                <div>
                  <span style={{ color: '#64748b' }}>หมวดหมู่ย่อย:</span>
                  <br />
                  <strong>{caseItem.sub_category || '-'}</strong>
                </div>
                <div>
                  <span style={{ color: '#64748b' }}>ผู้รายงาน:</span>
                  <br />
                  <strong>
                    {caseItem.is_anonymous
                      ? '🔒 นิรนาม (Anonymous)'
                      : `${caseItem.reporter_name || '-'} (${caseItem.reporter_role || '-'})`}
                  </strong>
                </div>
              </div>

              {/* Patient Banner */}
              {caseItem.is_patient_involved && (
                <div
                  style={{
                    backgroundColor: '#f0f9ff',
                    border: '1.5px solid #bae6fd',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    fontSize: '0.9em'
                  }}
                >
                  <span style={{ fontSize: '22px' }}>👤</span>
                  <div>
                    <strong style={{ color: '#0369a1' }}>ผู้ป่วยเกี่ยวข้องโดยตรง:</strong> HN{' '}
                    <strong>{caseItem.patient_hn || 'ไม่ระบุ'}</strong>
                    {caseItem.procedure_name && (
                      <span>
                        {' '}
                        | หัตถการ: <strong>{caseItem.procedure_name}</strong>
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Recall Warning Banner */}
              {isCriticalOrSterile && (
                <div
                  style={{
                    padding: '14px 16px',
                    backgroundColor: '#fef2f2',
                    border: '1.5px solid #f87171',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '10px'
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontWeight: '800',
                        color: '#991b1b',
                        fontSize: '0.92em',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <span>🚨</span>
                      <span>พบความเสี่ยงด้านการฆ่าเชื้อ / อุบัติการณ์วิกฤต (Critical Event)</span>
                    </div>
                    <div style={{ fontSize: '0.8em', color: '#b91c1c', marginTop: '2px' }}>
                      {caseItem.recall_batch_triggered
                        ? '✅ มีคำสั่งเปิดเคส Recall เรียกคืนวัสดุและเครื่องมือในรอบนี้แล้ว'
                        : 'สามารถสั่งเปิดเคส Recall เพื่อกักกันและเรียกคืนเครื่องมือในรอบนึ่ง/ล็อตนี้ทันที'}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => onTriggerRecall?.(caseItem)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#dc2626',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '800',
                      fontSize: '0.86em',
                      cursor: 'pointer',
                      boxShadow: '0 2px 6px rgba(220, 38, 38, 0.3)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <span>🚨</span>
                    <span>{caseItem.recall_batch_triggered ? 'ดูเคส Recall ในระบบ' : 'สั่งเปิดเคส Recall ทันที'}</span>
                  </button>
                </div>
              )}

              {/* Description & Immediate Action */}
              <div>
                <h4 style={{ margin: '0 0 6px 0', fontSize: '0.95em', color: '#1e293b', fontWeight: '800' }}>
                  📝 รายละเอียดเหตุการณ์ที่เกิดขึ้น
                </h4>
                <div
                  style={{
                    backgroundColor: '#ffffff',
                    padding: '14px',
                    borderRadius: '10px',
                    border: '1.5px solid #e2e8f0',
                    lineHeight: '1.6',
                    fontSize: '0.9em',
                    color: '#334155',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {caseItem.description}
                </div>
              </div>

              {caseItem.immediate_action && (
                <div>
                  <h4 style={{ margin: '0 0 6px 0', fontSize: '0.95em', color: '#1e293b', fontWeight: '800' }}>
                    ⚡ การแก้ไขปัญหาเบื้องต้นเฉพาะหน้า (Immediate Action Taken)
                  </h4>
                  <div
                    style={{
                      backgroundColor: '#f0fdf4',
                      padding: '14px',
                      borderRadius: '10px',
                      border: '1.5px solid #bbf7d0',
                      lineHeight: '1.6',
                      fontSize: '0.9em',
                      color: '#166534',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {caseItem.immediate_action}
                  </div>
                </div>
              )}

              {caseItem.contributing_factors && (
                <div>
                  <h4 style={{ margin: '0 0 6px 0', fontSize: '0.95em', color: '#1e293b', fontWeight: '800' }}>
                    🔍 ปัจจัยแวดล้อมที่ส่งผล (Contributing Factors)
                  </h4>
                  <div
                    style={{
                      backgroundColor: '#fffbeb',
                      padding: '12px 14px',
                      borderRadius: '10px',
                      border: '1.5px solid #fde68a',
                      fontSize: '0.88em',
                      color: '#92400e'
                    }}
                  >
                    {caseItem.contributing_factors}
                  </div>
                </div>
              )}

              {/* Evidence Photos Gallery */}
              {caseItem.image_urls && caseItem.image_urls.length > 0 && (
                <div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '0.95em', color: '#1e293b', fontWeight: '800' }}>
                    📸 ภาพถ่ายหลักฐาน ({caseItem.image_urls.length} ภาพ)
                  </h4>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {caseItem.image_urls.map((rawUrl, idx) => {
                      const imgObj =
                        typeof rawUrl === 'string'
                          ? { full_url: rawUrl, thumb_url: rawUrl, filename: `evidence_${idx + 1}.jpg` }
                          : rawUrl;
                      const previewSrc = imgObj.thumb_url || imgObj.full_url || imgObj.url || '';
                      const hasNasFull =
                        !!imgObj.full_url &&
                        (imgObj.storage_backend?.includes('synology') ||
                          imgObj.full_url.includes('risk-api') ||
                          imgObj.full_url.includes('synology'));

                      return (
                        <div
                          key={idx}
                          onClick={() => onOpenLightbox?.(imgObj)}
                          style={{
                            position: 'relative',
                            width: '100px',
                            height: '100px',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            border: '1px solid #cbd5e1',
                            cursor: 'zoom-in',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                            backgroundColor: '#f1f5f9'
                          }}
                        >
                          <img
                            src={previewSrc}
                            alt={`หลักฐาน ${idx + 1}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                          {hasNasFull && (
                            <span
                              style={{
                                position: 'absolute',
                                bottom: '2px',
                                right: '2px',
                                backgroundColor: 'rgba(13, 148, 136, 0.9)',
                                color: '#ffffff',
                                fontSize: '0.58em',
                                fontWeight: '800',
                                padding: '1px 4px',
                                borderRadius: '3px'
                              }}
                            >
                              NAS 4K
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ================= TAB 2: RCA & CAPA ================= */}
          {activeTab === 'rca' && (
            <form onSubmit={handleSaveRca} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '14px'
                }}
              >
                <div>
                  <label style={{ display: 'block', fontSize: '0.85em', fontWeight: '700', color: '#1e293b', marginBottom: '5px' }}>
                    สถานะการจัดการเคส
                  </label>
                  <select
                    value={investigationData.status}
                    onChange={(e) =>
                      setInvestigationData({ ...investigationData, status: e.target.value })
                    }
                    className="risk-select"
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  >
                    <option value="pending">🟡 รอตรวจสอบ (Pending)</option>
                    <option value="investigating">🔵 กำลังสอบสวน RCA (Investigating)</option>
                    <option value="resolved">🟢 ดำเนินการแก้ไขแล้ว (Resolved)</option>
                    <option value="closed">⚪ ปิดเคสสมบูรณ์ (Closed)</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.85em', fontWeight: '700', color: '#1e293b', marginBottom: '5px' }}>
                    ผู้รับผิดชอบสอบสวน / คณะกรรมการ
                  </label>
                  <input
                    type="text"
                    className="risk-search-input"
                    placeholder="ระบุชื่อผู้สอบสวน หรือทีมงานความเสี่ยง"
                    value={investigationData.investigator_name}
                    onChange={(e) =>
                      setInvestigationData({ ...investigationData, investigator_name: e.target.value })
                    }
                    style={{ paddingLeft: '12px' }}
                  />
                </div>
              </div>

              {/* Fishbone Category Selector */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85em', fontWeight: '700', color: '#1e293b', marginBottom: '6px' }}>
                  ผังก้างปลา (Fishbone / Ishikawa 5M Category)
                </label>
                <div className="risk-fishbone-grid">
                  {RCA_FISHBONE_CATEGORIES.map((cat) => {
                    const isSelected = investigationData.rca_fishbone_category === cat.id;
                    return (
                      <button
                        type="button"
                        key={cat.id}
                        className={`risk-fishbone-btn ${isSelected ? 'risk-fishbone-btn--active' : ''}`}
                        onClick={() =>
                          setInvestigationData({
                            ...investigationData,
                            rca_fishbone_category: isSelected ? '' : cat.id
                          })
                        }
                      >
                        <span>{cat.icon}</span>
                        <span>{cat.label.split(' (')[0]}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Root Cause Analysis Textarea */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85em', fontWeight: '700', color: '#1e293b', marginBottom: '5px' }}>
                  ผลการวิเคราะห์สาเหตุที่แท้จริง (Root Cause Analysis - RCA)
                </label>
                <textarea
                  rows="4"
                  className="risk-scrollbar"
                  placeholder="วิเคราะห์สาเหตุเชิงระบบ เช่น ปัญหาอุปกรณ์, ขั้นตอนการปฏิบัติงาน, การสื่อสาร, การจัดซื้อ..."
                  value={investigationData.root_cause_analysis}
                  onChange={(e) =>
                    setInvestigationData({ ...investigationData, root_cause_analysis: e.target.value })
                  }
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.9em',
                    boxSizing: 'border-box',
                    lineHeight: '1.5',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              {/* Corrective & Preventive Action Textarea */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85em', fontWeight: '700', color: '#1e293b', marginBottom: '5px' }}>
                  มาตรการแก้ไขและป้องกันการเกิดซ้ำ (Corrective & Preventive Action - CAPA)
                </label>
                <textarea
                  rows="4"
                  className="risk-scrollbar"
                  placeholder="ระบุแผนการปรับปรุงระบบ เช่น ปรับแก้ SOP, จัดอบรมบุคลากร, ปรับปรุงเครื่องมือ..."
                  value={investigationData.corrective_action}
                  onChange={(e) =>
                    setInvestigationData({ ...investigationData, corrective_action: e.target.value })
                  }
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.9em',
                    boxSizing: 'border-box',
                    lineHeight: '1.5',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
                <button
                  type="submit"
                  disabled={isSavingInvestigation}
                  style={{
                    padding: '10px 22px',
                    borderRadius: '10px',
                    backgroundColor: '#2563eb',
                    color: '#ffffff',
                    border: 'none',
                    fontWeight: '700',
                    fontSize: '0.92em',
                    cursor: isSavingInvestigation ? 'not-allowed' : 'pointer',
                    opacity: isSavingInvestigation ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 2px 6px rgba(37, 99, 235, 0.3)'
                  }}
                >
                  {isSavingInvestigation ? (
                    <>
                      <span
                        className="spinner"
                        style={{
                          display: 'inline-block',
                          width: '14px',
                          height: '14px',
                          border: '2px solid #fff',
                          borderTopColor: 'transparent',
                          borderRadius: '50%',
                          animation: 'spin 0.8s linear infinite'
                        }}
                      />
                      <span>กำลังบันทึก...</span>
                    </>
                  ) : (
                    <>
                      <span>💾</span>
                      <span>บันทึกผลการสอบสวน RCA & CAPA</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* ================= TAB 3: CSSD TRACEABILITY ================= */}
          {activeTab === 'cssd' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div
                style={{
                  backgroundColor: '#f0fdfa',
                  border: '1.5px solid #99f6e4',
                  borderRadius: '14px',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0f766e', fontWeight: '800' }}>
                  <span style={{ fontSize: '20px' }}>🧼</span>
                  <span>ข้อมูลการตรวจสอบย้อนกลับเครื่องมือและการฆ่าเชื้อ (CSSD Traceability)</span>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                    gap: '12px',
                    backgroundColor: '#ffffff',
                    padding: '14px',
                    borderRadius: '10px',
                    border: '1px solid #ccfbf1',
                    fontSize: '0.9em'
                  }}
                >
                  <div>
                    <span style={{ color: '#0f766e', fontWeight: '600' }}>🏷️ บาร์โค้ดเครื่องมือ:</span>
                    <br />
                    <strong style={{ fontSize: '1.05em', color: '#0f172a' }}>
                      {caseItem.instrument_barcode || 'ไม่มีข้อมูล'}
                    </strong>
                  </div>
                  <div>
                    <span style={{ color: '#0f766e', fontWeight: '600' }}>🔄 รหัสรอบนึ่ง:</span>
                    <br />
                    <strong style={{ fontSize: '1.05em', color: '#0f172a' }}>
                      {caseItem.sterile_cycle_id || 'ไม่มีข้อมูล'}
                    </strong>
                  </div>
                  <div>
                    <span style={{ color: '#0f766e', fontWeight: '600' }}>⚙️ รหัสเครื่องนึ่ง:</span>
                    <br />
                    <strong style={{ fontSize: '1.05em', color: '#0f172a' }}>
                      {caseItem.machine_id || 'ไม่มีข้อมูล'}
                    </strong>
                  </div>
                </div>
              </div>

              {/* Recall Trigger Section */}
              <div
                style={{
                  backgroundColor: '#fef2f2',
                  border: '1.5px solid #fecaca',
                  borderRadius: '14px',
                  padding: '18px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#b91c1c', fontWeight: '800' }}>
                  <span style={{ fontSize: '22px' }}>🚨</span>
                  <span>ระบบสั่งการเรียกคืนวัสดุและเครื่องมือ (CSSD Recall Protocol)</span>
                </div>
                <p style={{ margin: 0, fontSize: '0.88em', color: '#7f1d1d', lineHeight: '1.5' }}>
                  หากพบว่าเครื่องมือในรอบนึ่งนี้ไม่ผ่านเกณฑ์สเตอไรล์ (Chemical / Biological Indicator Fail) หรือมีเหตุชำรุดร้ายแรง
                  คุณสามารถสั่งเปิดเคส Recall เพื่อแจ้งเตือนและระงับการใช้งานเครื่องมือในรอบนี้ทั้งหมด
                </p>

                <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '4px' }}>
                  <button
                    type="button"
                    onClick={() => onTriggerRecall?.(caseItem)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#dc2626',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: '800',
                      fontSize: '0.9em',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(220, 38, 38, 0.25)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span>🚨</span>
                    <span>
                      {caseItem.recall_batch_triggered
                        ? 'ดูเคส Recall ที่เชื่อมโยงในระบบ'
                        : 'สั่งเปิดเคส Recall และกักกันเครื่องมือทันที'}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: '14px 24px',
            borderTop: '1px solid #e2e8f0',
            backgroundColor: '#f8fafc',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div style={{ fontSize: '0.82em', color: '#64748b' }}>
            อัปเดตล่าสุด:{' '}
            {caseItem.last_updated
              ? new Date(caseItem.last_updated).toLocaleString('th-TH')
              : '-'}
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 20px',
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              color: '#475569',
              border: '1px solid #cbd5e1',
              fontWeight: '600',
              fontSize: '0.88em',
              cursor: 'pointer'
            }}
          >
            ปิดหน้าต่าง
          </button>
        </div>
      </div>
    </div>
  );
}
